import axiosInstance from '../../../api/axios-instance';
import {
  Contact,
  ContactParam,
  GPanelContactsLabelsLogsResponse,
  Label,
  LabelGroupsResponse,
  LabelMutationResponse,
  LabelSync,
  LabelSyncUserStatus,
} from './shared/types';
import { ContactGroup, TemplateGenerationRequest } from '../../../../types';

export const GPanelContactsKeys = {
  LabelGroups: 'LabelGroups',
  OrganizationDirectoryContacts: 'OrganizationDirectoryContacts',
  LabelContacts: 'LabelContacts',
  GPanelContactCreate: 'GPanelContactCreate',
  TrashLabels: 'TrashLabels',
  TrashContacts: 'TrashContacts',
  AllSyncs: 'AllSyncs',
  LabelSyncs: 'LabelSyncs',
  SyncById: 'SyncById',
  SyncUsersStatus: 'SyncUsersStatus',
  GPanelLabels: 'GPanelLabels',
  GPanelContact: 'GPanelContact',
  GPanelContactEdit: 'GPanelContactEdit',
  GPanelContactDelete: 'GPanelContactDelete',
  GPanelContactImport: 'GPanelContactImport',
  GPanelLabelsAddContacts: 'GPanelLabelsAddContacts',
  GPanelLabelsRemoveContacts: 'GPanelLabelsRemoveContacts',
  GPanelLabelsLogs: 'GPanelLabelsLogs',
  GPanelShareContacts: 'GPanelShareContacts',
  GpanelContactImportContacts: 'GpanelContactImportContacts',
} as const;

export async function getLabels() {
  const res = await axiosInstance.get<Label[]>(`/api/gpanel-contacts/labels`);
  return res.data;
}

export async function getLabelGroups() {
  const res = await axiosInstance.get<LabelGroupsResponse>(
    `/api/gpanel-contacts/labels/group`
  );
  return res.data;
}

export async function getOrganizationDirectoryContacts() {
  const res = await axiosInstance.get<Contact[]>(
    `/api/gpanel-contacts/contacts-organization`
  );
  return res.data;
}

export async function getLabelContacts(labelId: number) {
  const res = await axiosInstance.get<Contact[]>(
    `/api/gpanel-contacts/contactsForLabel/${labelId}`
  );
  return res.data;
}

export async function createGPanelContact(
  request: ContactParam,
  changeLogMessage = ''
) {
  const res = await axiosInstance.post<Contact>(
    `/api/gpanel-contacts/contacts`,
    request,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function addLabel(name: string, changeLogMessage = '') {
  const res = await axiosInstance.post<LabelMutationResponse>(
    `/api/gpanel-contacts/labels`,
    {
      name,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function editLabel(
  id: number,
  name: string,
  changeLogMessage = ''
) {
  const res = await axiosInstance.put<LabelMutationResponse>(
    `/api/gpanel-contacts/labels/${id}`,
    {
      name,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function deleteLabel(id: number, changeLogMessage = '') {
  const res = await axiosInstance.delete<LabelMutationResponse>(
    `/api/gpanel-contacts/labels/trash/${id}`,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function getContact(id: string) {
  const res = await axiosInstance.get<Contact>(
    `/api/gpanel-contacts/contacts/${id}`
  );
  return res.data;
}

export async function editGPanelContact(
  request: ContactParam,
  id: string,
  changeLogMessage = ''
) {
  const resp = await axiosInstance.put<Contact>(
    `/api/gpanel-contacts/contacts/${id}`,
    request,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return resp.data;
}

export async function deleteGPanelContacts(
  contactIds: number[],
  changeLogMessage = ''
) {
  await axiosInstance.delete<void>(`/api/gpanel-contacts/contacts/trash`, {
    data: contactIds,
    params: {
      changeLogMessage,
    },
  });
}

export async function getTrashedLabels() {
  const res = await axiosInstance.get<Label[]>(
    `/api/gpanel-contacts/labels/trash`
  );
  return res.data;
}

export async function getTrashedContacts() {
  const res = await axiosInstance.get<Contact[]>(
    `/api/gpanel-contacts/contacts-trashed`
  );
  return res.data;
}

export async function restoreLabels(labelIds: number[], changeLogMessage = '') {
  const res = await axiosInstance.post<LabelMutationResponse>(
    `/api/gpanel-contacts/labels/restore/list`,
    labelIds,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function restoreContacts(
  contactIds: number[],
  changeLogMessage = ''
) {
  const res = await axiosInstance.post<LabelMutationResponse>(
    `/api/gpanel-contacts/contacts/restore`,
    contactIds,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function emptyLabelTrash(changeLogMessage = '') {
  const res = await axiosInstance.delete<LabelMutationResponse>(
    `/api/gpanel-contacts/labels/trash`,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function emptyContactsTrash(changeLogMessage = '') {
  const res = await axiosInstance.delete<LabelMutationResponse>(
    `/api/gpanel-contacts/contacts/empty-trash`,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function setLabelsToContact(
  contactId: string,
  labelIds: number[],
  labelNames: string[],
  changeLogMessage = ''
) {
  await axiosInstance.post<LabelMutationResponse>(
    `/api/gpanel-contacts/contacts/${contactId}/set-labels`,
    {
      labelIds,
      labelNames,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );
}

export async function getGPanelLabelsLogs(labelId: number) {
  const res = await axiosInstance.get<GPanelContactsLabelsLogsResponse[]>(
    `/api/gpanel-contacts/labels/logs/${labelId}`
  );
  return res.data;
}

export async function exportLabelLogs(labelId: number, changeLogMessage = '') {
  const res = await axiosInstance.post<string>(
    `/api/gpanel-contacts/labels/logs/${labelId}/export`,
    {},
    { params: { changeLogMessage } }
  );

  return res.data;
}

export async function manageContactsFromLabels(
  labelsToAdd: number[],
  labelsToRemove: number[],
  contactIds: number[],
  changeLogMessage = ''
) {
  await axiosInstance.post<LabelMutationResponse>(
    `/api/gpanel-contacts/labels/manage-contacts`,
    {
      labelsToAdd,
      labelsToRemove,
      contactIds,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );
}

export async function importContacts(
  userEmail: string,
  sourceLabel: string,
  targetLabel: number | null,
  sendNotification: boolean,
  newLabel: string,
  changeLogMessage: string
) {
  const response = await axiosInstance.post<Label | null>(
    '/api/gpanel-contacts/contacts/import',
    {
      userEmail,
      sourceLabel,
      targetLabel,
      sendNotification,
      newLabel,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );

  return response.data;
}

export async function generateTemplate(request: TemplateGenerationRequest) {
  const res = await axiosInstance.post<{ [key: string]: string }>(
    '/api/gpanel-contacts/template/generate',
    request
  );
  return res.data;
}

export async function getAllSyncs() {
  const res = await axiosInstance.get<LabelSync[]>(`/api/gpanel-contacts/sync`);
  return res.data;
}

export async function getSyncsByLabelId(labelId: string) {
  const res = await axiosInstance.get<LabelSync[]>(
    `/api/gpanel-contacts/sync/label/${labelId}`
  );
  return res.data;
}

export async function getSyncsById(syncId: string) {
  const res = await axiosInstance.get<LabelSync>(
    `/api/gpanel-contacts/sync/${syncId}`
  );
  return res.data;
}

export async function getSyncUsersStatus(syncId: string) {
  const res = await axiosInstance.get<LabelSyncUserStatus[]>(
    `/api/gpanel-contacts/sync/${syncId}/users-status`
  );
  return res.data;
}

export async function getImportContacts(userEmail: string) {
  const res = await axiosInstance.get<ContactGroup[]>(
    `/api/gpanel-contacts/import-contacts/${userEmail}`
  );
  return res.data;
}

export async function deleteSync(
  syncId: number,
  permanentlyDelete: boolean,
  changeLogMessage = ''
) {
  await axiosInstance.delete<void>(`/api/gpanel-contacts/sync/${syncId}`, {
    params: {
      permanentlyDelete,
      changeLogMessage,
    },
  });
}

export async function createSync(
  request: Partial<LabelSync>,
  changeLogMessage = ''
) {
  const res = await axiosInstance.post<LabelSync>(
    `/api/gpanel-contacts/sync`,
    request,
    {
      params: {
        changeLogMessage,
      },
    }
  );

  return res.data;
}

export async function editSync(
  request: Partial<LabelSync>,
  changeLogMessage = ''
) {
  const res = await axiosInstance.put<LabelSync>(
    `/api/gpanel-contacts/sync/${request.id}`,
    request,
    {
      params: {
        changeLogMessage,
      },
    }
  );

  return res.data;
}

/**
 * Restore a previously disconnected sync by ID
 * @param syncId The ID of the sync to restore
 */
export async function restoreSync(syncId: number): Promise<LabelSync> {
  const res = await axiosInstance.post<LabelSync>(
    `/api/gpanel-contacts/sync/${syncId}/restore`
  );
  return res.data;
}
